/**
 * Success dialog shown after a prompt-PR flow (edit-and-open-PR,
 * new-prompt-with-PR, or the single-PR add-assessment-input flow).
 * The paired-PR flows pass both URLs; single-PR flows pass just
 * ``pipelinesUrl``. Reused across surfaces for a consistent success
 * pattern.
 */
import React from "react";

import { Dialog } from "@fronterahealth/frontera-ui-components";

interface PROpenedDialogProps {
  pipelinesUrl: string | null;
  /** Optional. Present for paired-PR flows (new prompt + api-django
   *  companion). Omit for single-PR flows like the add-assessment-input
   *  case, and only the pipelines PR link renders. */
  apiDjangoUrl?: string | null;
  onClose: () => void;
  /** Optional label for the primary dismiss action. Defaults to "Done". */
  doneLabel?: string;
}

/**
 * Shown iff ``pipelinesUrl`` is non-null. The ``apiDjangoUrl`` link
 * only renders when its URL is non-null so single-PR flows don't show
 * an empty second card.
 */
export const PROpenedDialog: React.FC<PROpenedDialogProps> = ({
  pipelinesUrl,
  apiDjangoUrl,
  onClose,
  doneLabel = "Done",
}) => {
  const open = pipelinesUrl != null;
  const hasCompanion = apiDjangoUrl != null;
  return (
    <Dialog
      title={hasCompanion ? "PRs opened" : "PR opened"}
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      primaryButton={{
        text: doneLabel,
        onClick: onClose,
      }}
      // The Dialog component requires a secondaryButton; for a success
      // modal both actions dismiss so we point them at the same handler.
      secondaryButton={{
        text: "Close",
        onClick: onClose,
      }}
      size="lg"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {hasCompanion
            ? "Two PRs were opened for review. Both need to land before the change is live."
            : "The PR was opened for review."}
        </p>
        <a
          href={pipelinesUrl ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col gap-1 rounded-md border border-border-medium bg-bg-default px-4 py-3 hover:bg-limestone-50 transition-colors"
        >
          <span className="text-xs uppercase tracking-wide text-text-tertiary">
            {hasCompanion ? "Pipelines PR (primary)" : "Pipelines PR"}
          </span>
          <span className="text-sm text-text-primary underline break-all">{pipelinesUrl}</span>
        </a>
        {hasCompanion && (
          <a
            href={apiDjangoUrl ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-1 rounded-md border border-border-medium bg-bg-default px-4 py-3 hover:bg-limestone-50 transition-colors"
          >
            <span className="text-xs uppercase tracking-wide text-text-tertiary">api-django companion PR</span>
            <span className="text-sm text-text-primary underline break-all">{apiDjangoUrl}</span>
          </a>
        )}
      </div>
    </Dialog>
  );
};
